import React, { useEffect, useState, useRef } from "react";
import BlogPost from "./BlogPost";
import '../styles/Blog.css';
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {fetchBlogData} from "../utils/functions";

function Blogs() {
    const [t, i18n] = useTranslation();
    // Set pageSize: 1 on mobile, 2 on larger screens.
    const [pageSize, setPageSize] = useState(2);
    const [blogData, setBlogData] = useState([]);
    const [loading, setLoading] = useState(true);
    const location = useLocation();

    useEffect(() => {
        const updatePageSize = () => {
            if (window.innerWidth < 768) setPageSize(1);
            else setPageSize(2);
        };
        updatePageSize();
        window.addEventListener("resize", updatePageSize);
        return () => window.removeEventListener("resize", updatePageSize);
    }, []);

    useEffect(() => {
        const lang = location.pathname.startsWith('/hk') ? "zh-Hant-HK" : "en";

        const loadData = async () => {
            try {
                const data = await fetchBlogData(lang);
                setBlogData(data);
            } catch (error) {
                console.error('Error loading blog data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [location.pathname]);

    // Pagination state
    const [pageIndex, setPageIndex] = useState(0);
    const totalPages = Math.ceil(blogData.length / pageSize);

    // Reverse blogData so the most recent is first, then slice current page.
    const reversedBlogData = [...blogData].reverse();
    const currentBlogs = reversedBlogData.slice(
        pageIndex * pageSize,
        (pageIndex + 1) * pageSize
    );

    const [fade, setFade] = useState(false);

    // When pageIndex changes, trigger fade-out then fade-in
    useEffect(() => {
        setFade(true);
        const timer = setTimeout(() => {
            setFade(false);
        }, 200); // 200ms matches our CSS transition
        return () => clearTimeout(timer);
    }, [pageIndex]);

    const handleBlogPageClick = (newPage) => {
        setPageIndex(newPage);
    };

    const handlePrevBlogPage = () => {
        if (pageIndex > 0) {
            setPageIndex(pageIndex - 1);
        }
    };
    const handleNextBlogPage = () => {
        if (pageIndex < totalPages - 1) {
            setPageIndex(pageIndex + 1);
        }
    };

    /**
     * getTruncatedPages()
     * Returns an array of up to 5 elements (including ellipses)
     */
    const getTruncatedPages = () => {
        if (totalPages <= 1) return [0];
        if (totalPages <= 5) return [...Array(totalPages).keys()];

        const pages = [];
        const firstPage = 0;
        const lastPage = totalPages - 1;

        // Always show first page
        pages.push(firstPage);

        if (pageIndex <= 1) {
            // Near the start: show pages 0,1,2, ellipsis, last
            pages.push(1);
            pages.push(2);
            pages.push("...");
        } else if (pageIndex >= lastPage - 1) {
            // Near the end: show first, ellipsis, last-2, last-1, last
            pages.push("...");
            pages.push(lastPage - 2);
            pages.push(lastPage - 1);
        } else {
            // Middle: show first, ellipsis, current, ellipsis, last
            pages.push("...");
            pages.push(pageIndex);
            pages.push("...");
        }
        // Always show last page
        pages.push(lastPage);

        return pages;
    };

    // ----- Touch swipe logic for mobile -----
    const blogContainerRef = useRef(null);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const touchEndX = useRef(0);
    const touchEndY = useRef(0);
    const swipeDirectionRef = useRef(null);

    useEffect(() => {
        const container = blogContainerRef.current;
        if (!container) return;

        const handleTouchStart = (e) => {
            touchStartX.current = e.touches[0].clientX;
            touchStartY.current = e.touches[0].clientY;
            swipeDirectionRef.current = null; // Reset ref
        };

        const handleTouchMove = (e) => {
            const currentX = e.touches[0].clientX;
            const currentY = e.touches[0].clientY;
            touchEndX.current = currentX;
            touchEndY.current = currentY;

            // Determine direction if not yet set
            if (swipeDirectionRef.current === null) {
                const diffX = Math.abs(currentX - touchStartX.current);
                const diffY = Math.abs(currentY - touchStartY.current);
                if (diffX > diffY && diffX > 10) {
                    swipeDirectionRef.current = 'horizontal';
                } else if (diffY > diffX && diffY > 10) {
                    swipeDirectionRef.current = 'vertical';
                }
            }
            // Prevent vertical scrolling if swiping horizontally
            if (swipeDirectionRef.current === 'horizontal') {
                e.preventDefault();
            }
        };

        const handleTouchEnd = (e) => {
            if (e.target.closest('.blog-more-btn')) {
                swipeDirectionRef.current = null;
                return; // Skip pagination for "More" button
            }

            if (swipeDirectionRef.current === 'horizontal') {
                const diffX = touchStartX.current - touchEndX.current;
                if (Math.abs(diffX) > 50) {
                    if (diffX > 0) {
                        handleNextBlogPage();
                    } else {
                        handlePrevBlogPage();
                    }
                }
            }
            swipeDirectionRef.current = null; // Reset for next swipe
        };

        container.addEventListener("touchstart", handleTouchStart);
        container.addEventListener("touchmove", handleTouchMove, { passive: false });
        container.addEventListener("touchend", handleTouchEnd);
        return () => {
            container.removeEventListener("touchstart", handleTouchStart);
            container.removeEventListener("touchmove", handleTouchMove);
            container.removeEventListener("touchend", handleTouchEnd);
        };
    }, [pageIndex, totalPages]);

    return (
        <div className="">
            <div className="blog-section container">
                <div className="blog-header text-center">
                    <h2 className="section-main-title">
                        {t('blogs.title_before')}<mark className="green">{t('blogs.title_green')}</mark>{t('blogs.title_after')}</h2>
                </div>

                {/* Wrap blog posts in a container that supports swiping on mobile */}
                <div className={`blog-container container ${fade ? "fade-out" : "fade-in"}`} ref={blogContainerRef}>
                    {currentBlogs.map((blog, index) => (
                        <BlogPost key={index} blog={blog}/>
                    ))}
                </div>


                {totalPages > 1 && (
                    <div className="blog-pagination-container">
                        <button
                            className="blog-pagination-arrow"
                            onClick={handlePrevBlogPage}
                            disabled={pageIndex === 0}
                        >
                            &lt;
                        </button>

                        {getTruncatedPages().map((val, i) => {
                            if (val === "...") {
                                return (
                                    <button
                                        key={`ellipsis-${i}`}
                                        className="blog-pagination-button"
                                        disabled
                                        style={{ cursor: "default" }}
                                    >
                                        ...
                                    </button>
                                );
                            } else {
                                return (
                                    <button
                                        key={val}
                                        className={`blog-pagination-button ${
                                            pageIndex === val ? "blog-active-page" : ""
                                        }`}
                                        onClick={() => handleBlogPageClick(val)}
                                    >
                                        {val + 1}
                                    </button>
                                );
                            }
                        })}

                        <button
                            className="blog-pagination-arrow text-end"
                            onClick={handleNextBlogPage}
                            disabled={pageIndex === totalPages - 1}
                        >
                            &gt;
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Blogs;
